<template>
  <custom-dialog
    ref="dialog"
    store-modal
    color="secondary"
    width="600px"
    :icon="generateMessagePath('icon')"
    :title="generateMessagePath('title')"
  >
    <v-form
      ref="form"
      v-model="valid"
      @input="$emit('valid', valid)"
      lazy-validation
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12" sm="8" v-if="!isEditing">
            <custom-properties-selector
              :disabled="multi_property || multiPropertyIsForbidden"
              v-model="item.node_id"
              @newItem="handlePropertyChange"
              dense
              :rules="
                multi_property
                  ? []
                  : [
                      (v) =>
                        !!v || generateMessage('actionplans.create.noproperty'),
                    ]
              "
              :required="!multi_property"
            />
          </v-col>
          <v-col cols="12" sm="4" v-if="!isEditing">
            <v-checkbox
              v-if="!multiPropertyIsForbidden"
              class="ma-0 pa-0"
              label="Plan multi-installations"
              v-model="multi_property"
              hide-details
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" v-if="isEditing">
            <v-select
              dense
              hide-details
              outlined
              v-model="item.action_plan_status"
              :items="states"
              item-text="name"
              item-value="id"
              :label="generateMessage('actionplans.create.action_plan_status')"
            />
          </v-col>
          <v-col cols="12">
            <custom-origin-selector
              :action_plan_origins.sync="item.action_plan_origins"
            />
          </v-col>
          <v-col cols="12">
            <custom-text-area
              required
              v-model="item.description"
              :label="generateMessage('actionplans.create.description') + ' *'"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-autocomplete
              outlined
              :items="types"
              item-text="name"
              dense
              item-value="id"
              :rules="[
                (v) =>
                  (!!v && v !== 3) ||
                  generateMessage('actionplans.create.notype'),
              ]"
              v-model="item.action_plan_type"
              :label="generateMessage('actionplans.create.type') + ' *'"
              required
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6">
            <custom-entity-selector
              item-value="id"
              dense
              v-model="item.entity_id"
              required
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <template v-slot:actions>
      <v-btn
        :disabled="!valid"
        small
        color="success"
        class="mx-5"
        @click="handleValidateClick"
        :loading="isEditing ? isLoadingUpdate : isLoadingCreate"
      >
        {{ generateMessagePath('validate') }}
      </v-btn>
    </template>
    <list-action-not-ended
      v-model="showListActionNotEnded"
      :items="statusNotEnded"
      @clicked="validate"
    />
  </custom-dialog>
</template>

<script>
import CustomEntitySelector from '@/components/selects/CustomEntitySelector';
import CustomTextArea from '@/components/CustomTextArea';
import CustomOriginSelector from '@/components/selects/CustomOriginSelector';
import CustomPropertiesSelector from '@/components/selects/CustomPropertiesSelector';
import CustomDialog from '@/components/dialogs/CustomDialog';
import formComponent from '@/business/mixins/form';
import { mapActions, mapGetters } from 'vuex';
import { ACTIONPLAN_ACTIONS_LIST_ROUTE_NAME } from '@/router/actions.router';
import { generateMessage } from '@/utils/i18n';
import {
  formatDateToInputDateTime,
  getCurrentLocalDateTime,
} from '@/business/utils/dates';
import ListActionNotEnded from '@/components/dialogs/storeModal/modal/ListActionNotEnded';

export default {
  name: 'ActionplanFormDialog',
  mixins: [formComponent({ namespace: 'actionplans' })],
  computed: {
    ...mapGetters({
      types: 'actionplans/types/items',
      states: 'actionplans/status/items',
      newItem: 'actionplans/newItem',
      actions: 'actionplans/actions/items',
    }),
    statusNotEnded() {
      return this.actions.filter(
        (e) =>
          e.current_act_status_type_name !== 'Annulé' &&
          e.current_act_status_type_name !== 'Terminé'
      );
    },
  },
  components: {
    ListActionNotEnded,
    CustomPropertiesSelector,
    CustomOriginSelector,
    CustomDialog,
    CustomEntitySelector,
    CustomTextArea,
  },
  props: {
    multiPropertyIsForbidden: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      multi_property: false,
      ACTIONPLAN_ACTIONS_LIST_ROUTE_NAME,
      showListActionNotEnded: false,
    };
  },
  watch: {
    newItem(item) {
      if (item && item.id) {
        this.reset();
        this.$router.push({
          name: ACTIONPLAN_ACTIONS_LIST_ROUTE_NAME,
          params: { actionplanId: item.id },
        });
        this.$store.dispatch('alerts/initItems');
        this.$store.dispatch('modal/hideModal');
      }
    },
    updateItem(item) {
      if (item && item.id) {
        this.$store.dispatch(`actionplans/setCurrent`, item);
        this.$store.dispatch('modal/hideModal');
      }
    },
    multi_property(val) {
      if (val) this.item.node_id = null;
    },
    isEditing(val) {
      if (!val && !this.oldItem) {
        this.reset();
      }
    },
    oldItem(item) {
      if (!item && !this.isEditing) {
        this.reset();
      }
      if (item) {
        this.initItemFromPartialItem(item);
      }
    },
  },
  methods: {
    ...mapActions('modal', ['showModal']),
    handlePropertyChange(property) {
      if (property) this.item.entity_id = property.entity_id;
    },
    generateMessagePath(key) {
      return this.generateMessage(
        `actionplans.${this.isEditing ? 'update' : 'create'}.${key}`
      );
    },
    handleValidateClick() {
      if (
        this.item.action_plan_status === 3 &&
        this.statusNotEnded.length !== 0
      ) {
        this.showListActionNotEnded = true;
      } else {
        this.validate();
      }
    },
    generateMessage,
    getCurrentLocalDateTime,
    initActionPlanOrigins() {
      return [
        {
          origin_subtype_id: null,
          datetime: this.getCurrentLocalDateTime(),
        },
      ];
    },
    initItemFromNullItem() {
      this.item = {
        action_plan_origins: this.initActionPlanOrigins(),
        description: '',
        action_plan_type: null,
        node_id: null,
        entity_id: null,
      };
    },
    initItemFromPartialItem(partialItem) {
      let actionPlanOrigins;
      if (
        partialItem.action_plan_origins &&
        partialItem.action_plan_origins.length > 0
      ) {
        actionPlanOrigins = partialItem.action_plan_origins.slice();
        actionPlanOrigins = actionPlanOrigins.map((ori) => {
          return {
            ...ori,
            datetime: formatDateToInputDateTime(ori.datetime),
          };
        });
      } else {
        actionPlanOrigins = this.initActionPlanOrigins();
      }
      this.item = {
        action_plan_origins: actionPlanOrigins,
        description: partialItem.description,
        node_id: partialItem.node_id,
        action_plan_type: partialItem.action_plan_type,
        entity_id: partialItem.entity_id,
        action_plan_status: partialItem.action_plan_status,
        id: partialItem.id,
      };
    },
    reset() {
      this.initItemFromNullItem();
      this.$refs.form.resetValidation();
    },
  },
};
</script>
